import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useDisconnectedNetworksStore } from '@/stores/disconnectedNetworksStore';
import { getConnectionLink, syncMetricoolConnectionsToDb } from '@/services/data/socialConnections';
import { getUserById } from '@/services/data/users';
import type { Channel } from '@/services/business/calendar';

interface ConnectResult {
  success: boolean;
  loginUrl?: string;
  error?: string;
}

export function useSocialNetworkConnect() {
  const [isLoading, setIsLoading] = useState(false);
  const [currentNetwork, setCurrentNetwork] = useState<Channel | null>(null);

  const getConnectUrl = async (userId: string, _platform: Channel): Promise<ConnectResult> => {
    const userResult = await getUserById(userId);
    if (!userResult.success || !userResult.data?.metricool_blog_id) {
      return { success: false, error: 'Compte non configuré avec Metricool' };
    }
    const blogId = userResult.data.metricool_blog_id;
    const email = userResult.data.email ?? undefined;
    const result = await getConnectionLink(userId, blogId, { email });
    if (result.success && result.link) {
      return { success: true, loginUrl: result.link };
    }
    return { success: false, error: result.error || 'Erreur inconnue' };
  };

  const connectNetwork = async (platform: Channel, userId?: string): Promise<ConnectResult> => {
    const authState = useAuthStore.getState();
    const targetUserId = userId || authState.userId;
    
    if (!targetUserId) {
      return { success: false, error: 'No user ID available' };
    }

    setIsLoading(true);
    setCurrentNetwork(platform);

    try {
      const result = await getConnectUrl(targetUserId, platform);
      return result;
    } finally {
      setIsLoading(false);
      setCurrentNetwork(null);
    }
  };

  const refreshConnections = async (userId?: string) => {
    const authState = useAuthStore.getState();
    const targetUserId = userId || authState.userId;
    
    if (targetUserId) {
      // First sync with Metricool to update local DB
      console.log('[refreshConnections] Syncing with Metricool...');
      await syncMetricoolConnectionsToDb(targetUserId);
      
      // Then fetch disconnected networks (this will read from updated DB)
      await useDisconnectedNetworksStore.getState().fetchDisconnectedNetworks(targetUserId);
    }
  };

  return { 
    connectNetwork, 
    refreshConnections,
    isLoading, 
    currentNetwork 
  };
}
